import React, { useState, useEffect } from 'react';
import { userCompanyAPI } from '../services/api';
import '../styles/Admin.css';
import Icon from './Icon';

function UserConnectionsManager({ darkMode = true }) {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [addUserIdentifier, setAddUserIdentifier] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchConnections();
  }, []);

  const fetchConnections = async () => {
    try {
      setLoading(true);
      const response = await userCompanyAPI.getCompanyConnections();
      setConnections(response.data.connections || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load connections');
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (connectionId) => {
    if (!window.confirm('Are you sure you want to remove this user? They will lose company access.')) {
      return;
    }

    try {
      await userCompanyAPI.removeUserConnection(connectionId);
      setSuccess('User removed');
      fetchConnections();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove');
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!addUserIdentifier) return;
    try {
      await userCompanyAPI.addUserToCompany(addUserIdentifier);
      setSuccess('User added successfully!');
      setAddUserIdentifier('');
      fetchConnections();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add user');
    }
  };

  // Filter connections based on search
  const filteredConnections = connections.filter(conn => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      (conn.user_firstname && conn.user_firstname.toLowerCase().includes(term)) ||
      (conn.user_surname && conn.user_surname.toLowerCase().includes(term)) ||
      (conn.user_email && conn.user_email.toLowerCase().includes(term)) ||
      (conn.user_username && conn.user_username.toLowerCase().includes(term))
    );
  });

  if (loading) {
    return <div className="loading">Loading connections...</div>;
  }

  return (
    <div>
      {error && <div className="error" style={{marginBottom: '16px'}}>{error}</div>}
      {success && <div className="success" style={{marginBottom: '16px'}}>{success}</div>}

      {/* Add User Card */}
      <div className="card" style={{marginBottom:'24px'}}>
        <h2 style={{fontSize: '18px', marginBottom: '16px', color: darkMode ? '#ffffff' : '#111827', display: 'flex', alignItems: 'center', gap: '10px'}}>
          <Icon name="add_circle" color="primary" darkMode={darkMode} size={18} />
          Add user
        </h2>
        <p style={{fontSize:'14px', color: darkMode ? 'rgba(255,255,255,0.5)' : '#4b5563', marginBottom: '16px'}}>
          Enter the username or email. The connection will auto-accept and the user's bikes will register to the company.
        </p>
        <form onSubmit={handleAddUser} style={{display:'flex', gap:'12px', flexWrap:'wrap', alignItems:'center'}}>
          <input
            type="text"
            placeholder="username or email"
            value={addUserIdentifier}
            onChange={(e)=>setAddUserIdentifier(e.target.value)}
            style={{
              padding:'10px 16px',
              minWidth:'280px',
              borderRadius: '8px',
              border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #d1d5db',
              background: darkMode ? 'var(--bg-overlay-medium)' : '#ffffff',
              color: darkMode ? '#ffffff' : '#111827',
              fontSize: '14px'
            }}
          />
          <button type="submit" className="btn btn-primary">Add</button>
        </form>
      </div>

      {/* All Connections */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
          <h2 style={{fontSize: '18px', margin: 0, color: darkMode ? '#ffffff' : '#111827', display: 'flex', alignItems: 'center', gap: '10px'}}>
            <Icon name="link" color="primary" darkMode={darkMode} size={18} />
            All connections ({connections.length})
          </h2>
            <input
            type="text"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              padding: '8px 14px',
              borderRadius: '8px',
              border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #d1d5db',
              background: darkMode ? 'var(--bg-overlay-medium)' : '#ffffff',
              color: darkMode ? '#ffffff' : '#111827',
              fontSize: '13px',
              minWidth: '220px'
            }}
          />
        </div>
        
        {filteredConnections.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '40px 20px',
            color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280'
          }}>
            <Icon name="users" color="muted" darkMode={darkMode} size={48} style={{ marginBottom: '16px' }} />
            <p style={{ fontStyle: 'italic' }}>
              {searchTerm ? 'No matches found' : 'No connections yet'}
            </p>
          </div>
        ) : (
          <div style={{overflowX: 'auto'}}>
            <table style={{width: '100%', borderCollapse: 'collapse'}}>
              <thead>
                <tr style={{borderBottom: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e5e7eb'}}>
                  <th style={{padding: '12px', textAlign: 'left', color: darkMode ? 'rgba(255,255,255,0.7)' : '#4b5563', fontSize: '13px', fontWeight: '600'}}>User</th>
                  <th style={{padding: '12px', textAlign: 'left', color: darkMode ? 'rgba(255,255,255,0.7)' : '#4b5563', fontSize: '13px', fontWeight: '600'}}>Email</th>
                  <th style={{padding: '12px', textAlign: 'left', color: darkMode ? 'rgba(255,255,255,0.7)' : '#4b5563', fontSize: '13px', fontWeight: '600'}}>Status</th>
                  <th style={{padding: '12px', textAlign: 'left', color: darkMode ? 'rgba(255,255,255,0.7)' : '#4b5563', fontSize: '13px', fontWeight: '600'}}>Joined</th>
                  <th style={{padding: '12px', textAlign: 'left', color: darkMode ? 'rgba(255,255,255,0.7)' : '#4b5563', fontSize: '13px', fontWeight: '600'}}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredConnections.map(conn => (
                  <tr key={conn.id} style={{borderBottom: darkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid #f3f4f6'}}>
                    <td style={{padding: '12px', color: darkMode ? '#ffffff' : '#111827'}}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, var(--lime-primary) 0%, var(--secondary) 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#0a0a1a',
                          fontWeight: '600',
                          fontSize: '14px'
                        }}>
                          {(conn.user_firstname?.[0] || conn.user_username?.[0] || '?').toUpperCase()}
                        </div>
                        <div>
                          <div style={{ fontWeight: '500' }}>
                            {conn.user_firstname} {conn.user_surname}
                          </div>
                          <div style={{ fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280' }}>
                            @{conn.user_username}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={{padding: '12px', color: darkMode ? 'rgba(255,255,255,0.7)' : '#4b5563'}}>{conn.user_email}</td>
                    <td style={{padding: '12px'}}>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: '600',
                        background: conn.status === 'accepted' ? 'rgba(16, 185, 129, 0.15)' :
                                   conn.status === 'pending' ? 'rgba(245, 158, 11, 0.15)' :
                                   'rgba(107, 114, 128, 0.15)',
                        color: conn.status === 'accepted' ? '#4A8C18' :
                              conn.status === 'pending' ? '#fbbf24' :
                              darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280',
                        textTransform: 'capitalize'
                      }}>
                        {conn.status === 'accepted' ? 'Active' : conn.status === 'pending' ? 'Pending' : conn.status}
                      </span>
                    </td>
                    <td style={{padding: '12px', color: darkMode ? 'rgba(255,255,255,0.7)' : '#4b5563'}}>
                      {conn.requested_at ? new Date(conn.requested_at).toLocaleDateString('en-US') : '-'}
                    </td>
                    <td style={{padding: '12px'}}>
                      <button 
                        className="btn btn-sm" 
                        onClick={() => handleRemove(conn.id)}
                        style={{
                          padding: '6px 12px',
                          fontSize: '12px',
                          background: 'rgba(239, 68, 68, 0.15)',
                          color: '#f87171',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer'
                        }}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default UserConnectionsManager;
